import { useMutation, type InfiniteData } from "@tanstack/react-query";
import { defineMessages, useIntl } from "react-intl";

import { queryClient } from "@/api/core";
import { useApi } from "@/hooks";
import { boostSchema, type Boost } from "@/schemas";
import toast from "@/toast";
import type { PaginatedResult } from "@/utils/queries";

import { BoostQueryKeys } from "../query-keys";
import { updateBoostInList } from "../utils";

import type { AxiosError, AxiosResponse } from "axios";

const messages = defineMessages({
  cancelError: {
    id: "boosts.cancel.hook_error",
    defaultMessage: "Couldn't cancel boost. Please try again.",
  },
});

type CancelBoostVariables = { uuid: string };
type CancelBoostContext = {
  previousDetail: Boost | undefined;
  previousList: InfiniteData<PaginatedResult<Boost>> | undefined;
};

const CANCEL_BOOST_MUTATION_KEY = ["cancelBoost"] as const;

function useCancelBoost() {
  const api = useApi();
  const intl = useIntl();

  return useMutation<
    AxiosResponse<unknown>,
    AxiosError,
    CancelBoostVariables,
    CancelBoostContext
  >({
    mutationKey: CANCEL_BOOST_MUTATION_KEY,
    mutationFn: ({ uuid }) => api.post(`/api/v1/boosts/${uuid}/cancel`),
    async onMutate({ uuid }) {
      await queryClient.cancelQueries({ queryKey: BoostQueryKeys.list() });
      await queryClient.cancelQueries({ queryKey: BoostQueryKeys.detail(uuid) });

      const previousDetail = queryClient.getQueryData<Boost>(
        BoostQueryKeys.detail(uuid),
      );
      const previousList = queryClient.getQueryData<
        InfiniteData<PaginatedResult<Boost>>
      >(BoostQueryKeys.list());

      if (previousDetail) {
        queryClient.setQueryData<Boost>(BoostQueryKeys.detail(uuid), {
          ...previousDetail,
          state: "canceled",
        });
      }
      updateBoostInList(uuid, { state: "canceled" });

      return { previousDetail, previousList };
    },
    onError(_err, { uuid }, context) {
      if (context?.previousDetail) {
        queryClient.setQueryData<Boost>(
          BoostQueryKeys.detail(uuid),
          context.previousDetail,
        );
      }
      if (context?.previousList) {
        queryClient.setQueryData<InfiniteData<PaginatedResult<Boost>>>(
          BoostQueryKeys.list(),
          context.previousList,
        );
      }
      toast.error(intl.formatMessage(messages.cancelError));
    },
    onSuccess(response, { uuid }) {
      const parsed = boostSchema.safeParse(response.data);
      if (parsed.success) {
        queryClient.setQueryData<Boost>(
          BoostQueryKeys.detail(uuid),
          parsed.data,
        );
      } else {
        queryClient.invalidateQueries({ queryKey: BoostQueryKeys.detail(uuid) });
      }
      queryClient.invalidateQueries({
        queryKey: BoostQueryKeys.list(),
        refetchType: "none",
      });
      queryClient.invalidateQueries({
        queryKey: BoostQueryKeys.eligibilityRoot(),
        refetchType: "none",
      });
    },
  });
}

export { useCancelBoost, CANCEL_BOOST_MUTATION_KEY, type CancelBoostVariables };
